import React, { useEffect } from 'react';
import { Wallet, CreditCard, History } from 'lucide-react';
import { trackStoreClick } from '../lib/analytics';
import SEO from '../components/SEO';
import { getPageSeo, breadcrumbSchema, appSchema } from '../lib/seo';

const APPLE_BADGE = '/images/apps/apple-badge.webp';
const GOOGLE_BADGE = '/images/apps/google-badge.png';
const APP_HERO_IMAGE = '/images/apps/apps-hero.png';
const TAP_NFC_IMAGE = '/images/apps/iphone-tap-on-top.png';

const APPLE_STORE_URL = 'https://apps.apple.com/us/app/wristcoin/id1462360007';
const GOOGLE_STORE_URL = 'https://play.google.com/store/apps/details?id=com.mywristcoin.balancechecker';

const StoreBadges = ({ className = '', location = 'apps' }) => (
  <div className={`flex flex-wrap items-center gap-4 ${className}`}>
    <a
      href={APPLE_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackStoreClick('apple', location)}
      className="transition-transform duration-200 hover:scale-105"
      aria-label="Download on the App Store"
    >
      <img
        src={APPLE_BADGE}
        alt="Download on the App Store"
        className="h-14 w-auto object-contain"
      />
    </a>
    <a
      href={GOOGLE_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackStoreClick('google', location)}
      className="transition-transform duration-200 hover:scale-105"
      aria-label="Get it on Google Play"
    >
      <img
        src={GOOGLE_BADGE}
        alt="Get it on Google Play"
        className="h-14 w-auto object-contain"
      />
    </a>
  </div>
);

const Apps = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    { icon: Wallet, label: 'Check Balance' },
    { icon: CreditCard, label: 'Load Money' },
    { icon: History, label: 'View Transactions' },
  ];

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        {...getPageSeo("/apps")}
        schema={[
          appSchema(),
          breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Guest App", path: "/apps" },
        ]),
        ]}
      />
      {/* Page Title */}
      <section className="pt-32 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight">
            Guest/Attendee App
          </h1>
          <div className="mt-6 h-1 w-20 bg-gradient-to-r from-[#009688] to-[#4EBAAA] rounded-full" />
        </div>
      </section>

      {/* Top Store Badges */}
      <section className="pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <StoreBadges location="apps_top" />
        </div>
      </section>

      {/* Download our guest/attendee app */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl md:text-5xl font-bold leading-tight mb-8">
                Download our{' '}
                <span className="bg-gradient-to-r from-[#009688] to-[#4EBAAA] bg-clip-text text-transparent">
                  guest/attendee
                </span>{' '}
                app
              </h2>
              <ul className="space-y-5 mb-10">
                {features.map(({ icon: Icon, label }) => (
                  <li
                    key={label}
                    className="flex items-center gap-4 text-lg md:text-xl text-white/90"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#009688]/15 border border-[#009688]/30">
                      <Icon size={20} className="text-[#009688]" />
                    </span>
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
              <StoreBadges location="apps_hero" />
            </div>

            <div className="order-1 md:order-2 flex justify-center">
              <img
                src={APP_HERO_IMAGE}
                alt="Download our guest/attendee app"
                className="w-full max-w-lg h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TAP NFC WRISTBAND and go! */}
      <section className="py-16 md:py-24 bg-[#0b1820] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center">
              <img
                src={TAP_NFC_IMAGE}
                alt="TAP NFC WRISTBAND and go!"
                className="w-full max-w-lg h-auto"
              />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="block">TAP NFC</span>
                <span className="block">
                  <span className="bg-gradient-to-r from-[#009688] to-[#4EBAAA] bg-clip-text text-transparent">
                    WRISTBAND
                  </span>{' '}
                  and go!
                </span>
              </h2>
              <p className="mt-6 text-lg text-white/70 max-w-md">
                One quick tap connects your wristband with your phone — check
                your balance, top up, and track every transaction on the go.
              </p>
              <div className="mt-10">
                <StoreBadges location="apps_nfc" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Apps;
